import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import TabloBoard from '../components/TabloBoard';
import {
  extractTabloState,
  fetchTabloOverlay,
  isTabloClosedPayload,
  mergeTabloOverlayState,
  normalizeTabloScene,
  subscribeTablo,
  TABLO_EVENT_CARD_MS,
  resolveTabloEventPulses,
  resolveTabloResultPulseMs,
} from '../utilities/tabloApi';
import { useI18n } from '../utilities/i18n';
import '../styles/tablo.css';

/**
 * OBS Browser Source: iframe.srcdoc = layers[…].html
 * Scoreboard — независимый слой (scoreboard_visible); с preview уходит под него.
 * pulse:"event" → layers.event ~6с.
 * pulse:"result" → подробный результат ~6с (после гола: событие → пауза 2с → result).
 */
export default function TabloOverlay() {
  const { overlayKey } = useParams();
  const { t } = useI18n();
  const [state, setState] = useState(null);
  const [error, setError] = useState('');
  const [closed, setClosed] = useState(false);
  const [eventVisible, setEventVisible] = useState(false);
  const [resultPulse, setResultPulse] = useState(false);
  const eventTimer = useRef(null);
  const resultTimer = useRef(null);

  useEffect(() => {
    document.documentElement.classList.add('tablo-overlay-root');
    document.body.classList.add('tablo-overlay-root');
    return () => {
      document.documentElement.classList.remove('tablo-overlay-root');
      document.body.classList.remove('tablo-overlay-root');
      if (eventTimer.current) clearTimeout(eventTimer.current);
      if (resultTimer.current) clearTimeout(resultTimer.current);
    };
  }, []);

  const clearPulse = (kind) => {
    setState((prev) => (
      prev && prev.pulse === kind ? { ...prev, pulse: null } : prev
    ));
  };

  const armPulses = (next) => {
    const pulsesOn = resolveTabloEventPulses(next, true);
    // Только явный pulse открывает сцены. Обычный refresh (замена, фол, …) — не трогаем.
    if (next?.pulse === 'event') {
      if (!pulsesOn) {
        setEventVisible(false);
        clearPulse('event');
        return;
      }
      if (resultTimer.current) {
        clearTimeout(resultTimer.current);
        resultTimer.current = null;
      }
      if (eventTimer.current) {
        clearTimeout(eventTimer.current);
        eventTimer.current = null;
      }
      setResultPulse(false);
      setEventVisible(true);
      eventTimer.current = setTimeout(() => {
        eventTimer.current = null;
        setEventVisible(false);
        clearPulse('event');
      }, TABLO_EVENT_CARD_MS);
      return;
    }

    if (next?.pulse === 'result') {
      if (!pulsesOn) {
        setResultPulse(false);
        clearPulse('result');
        return;
      }
      if (eventTimer.current) {
        clearTimeout(eventTimer.current);
        eventTimer.current = null;
      }
      if (resultTimer.current) {
        clearTimeout(resultTimer.current);
        resultTimer.current = null;
      }
      setEventVisible(false);
      setResultPulse(true);
      resultTimer.current = setTimeout(() => {
        resultTimer.current = null;
        setResultPulse(false);
        clearPulse('result');
      }, resolveTabloResultPulseMs(next));
      return;
    }

    // Нет pulse — не сбрасываем уже идущую карточку/result от предыдущего гола/ЖК
    if (eventTimer.current || resultTimer.current) return;
    setResultPulse(false);
    setEventVisible(normalizeTabloScene(next?.scene) === 'event');
  };

  useEffect(() => {
    const key = String(overlayKey || '').trim();
    if (!key) {
      setError('overlay_key missing');
      return undefined;
    }

    let cancelled = false;
    setError('');
    setClosed(false);

    (async () => {
      try {
        const data = await fetchTabloOverlay(key);
        if (cancelled) return;
        const next = extractTabloState(data) || data;
        if (isTabloClosedPayload(next)) {
          setClosed(true);
          setState(next);
          return;
        }
        setState(next);
        armPulses(next);
      } catch (err) {
        if (!cancelled) {
          if (err?.response?.status === 404) {
            setClosed(true);
            setState({ closed: true, reason: 'closed' });
            return;
          }
          const msg = err?.response?.data;
          setError(typeof msg === 'string' ? msg : (err?.message || 'Tablo not found'));
        }
      }
    })();

    const cleanup = subscribeTablo({
      overlayKey: key,
      onUpdate: (next) => {
        if (cancelled) return;
        if (isTabloClosedPayload(next)) {
          setClosed(true);
          setState(next);
          return;
        }
        if (next?.overlay_key && String(next.overlay_key) !== key) return;
        setClosed(false);
        // Slim pulse часто шлёт только event+scoreboard — не затираем pack-слой
        setState((prev) => mergeTabloOverlayState(prev, next));
        setError('');
        armPulses(next);
      },
      onAck: (ack) => {
        if (cancelled) return;
        if (ack && ack.ok === false) {
          setClosed(true);
        }
      },
    });

    return () => {
      cancelled = true;
      cleanup();
      if (eventTimer.current) clearTimeout(eventTimer.current);
      if (resultTimer.current) clearTimeout(resultTimer.current);
    };
  }, [overlayKey]);

  if (closed) {
    return <div className="tablo-overlay-page is-closed" />;
  }

  const waiting = !state?.layers && !state?.layer_urls && !state?.match;
  const boardState = resultPulse && state
    ? { ...state, scene: 'result', picture_type: 'result' }
    : state;

  return (
    <div className="tablo-overlay-page">
      {error && waiting ? (
        <div className="tablo-overlay-error">{error || t('tabloClosed')}</div>
      ) : (
        <TabloBoard state={boardState} eventVisible={eventVisible} />
      )}
    </div>
  );
}
